import { useEffect, useState } from 'react'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { TransactionJson } from 'app/types/entities'
import { computePreferredProvidersFromTransactions, type PreferredProvider } from 'lib/preferredProviders'
import { logError } from 'lib/logging'

// One visit produces several procedure rows, so this covers roughly the last
// ~50 visits of history.
const TRANSACTION_LIMIT = 250

export type PreferredProviders = {
  preferredProvider?: PreferredProvider
  preferredHygienist?: PreferredProvider
  loading: boolean
}

// Derived from Procedure transactions rather than appointments: appointment
// procedureCodes are unpopulated for most synced history, and transactions
// attribute each procedure to the provider who actually performed it.
export function usePreferredProviders(patientId?: string): PreferredProviders {
  const [state, setState] = useState<PreferredProviders>({ loading: !!patientId })

  useEffect(() => {
    if (!patientId) {
      setState({ loading: false })
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true }))
    const load = async () => {
      try {
        const { hits } = await gqlFetchList<TransactionJson>(`transactions`, {
          search: `patientId:${patientId} AND transactionType:Procedure`,
          sort: { field: `transactionDate`, direction: `desc` },
          pagination: { limit: TRANSACTION_LIMIT },
        })
        if (cancelled) return
        const { preferredProvider, preferredHygienist } = computePreferredProvidersFromTransactions(hits)
        setState({ preferredProvider, preferredHygienist, loading: false })
      } catch (e) {
        if (cancelled) return
        logError(`usePreferredProviders: failed to fetch procedure transactions`, e)
        setState({ loading: false })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [patientId])

  return state
}
